import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import t from 'ember-intl/helpers/t';
import { Section } from 'tio-ui/components/layout';
import Table from 'tio-ui/components/table';
import Popover from 'tio-ui/components/popover';
import { PhInfo } from 'ember-phosphor-icons';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';

const PSLF_0726_APP = 'PSLF-0726';

interface UsageHistorySignature {
  Args: {
    recommendations: RecommendationModel[];
  };
  Element: HTMLElement;
}

const UsageHistory: TOC<UsageHistorySignature> = <template>
  <Section data-test-pslf-0726-history ...attributes>
    <:header>{{t "forgiveness_estimator_0726.history.title"}}</:header>
    <:body>
      <Table @isLoading={{false}}>
        <:thead as |options|>
          <options.tr>
            <options.th>{{t "forgiveness_estimator_0726.history.date"}}</options.th>
            <options.th>{{t "forgiveness_estimator_0726.history.tool"}}</options.th>
            <options.th>{{t "forgiveness_estimator_0726.history.view"}}</options.th>
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{#each @recommendations as |rec|}}
            <options.tr data-test-pslf-0726-history-row={{rec.requestingApp}}>
              <options.td>
                {{safeFormatDate rec.createdAt month="short" day="numeric" year="numeric"}}
              </options.td>
              <options.td>
                {{#if (eq rec.requestingApp PSLF_0726_APP)}}
                  {{t "forgiveness_estimator_0726.history.tool_pslf_0726"}}
                {{else}}
                  <span class="tio-row gap-1">
                    {{t "forgiveness_estimator_0726.history.tool_pslf_legacy"}}
                    <Popover @triggerMode="hover">
                      <:trigger as |pop|>
                        <span
                          tabindex="0"
                          {{pop.register}}
                          {{pop.hoverHandlers}}
                          data-test-pslf-0726-history-info
                        >
                          <PhInfo class="tio-fg-secondary" @size={{16}} />
                        </span>
                      </:trigger>
                      <:body>
                        <p class="text-sm max-w-xs">
                          {{t "forgiveness_estimator_0726.history.legacy_info"}}
                        </p>
                      </:body>
                    </Popover>
                  </span>
                {{/if}}
              </options.td>
              <options.td>
                {{#if (eq rec.requestingApp PSLF_0726_APP)}}
                  <LinkTo
                    @route="authenticated.pslf.dashboard.forgiveness-estimator.results"
                    @model={{rec.id}}
                    class="tio-copy"
                  >
                    {{t "forgiveness_estimator_0726.history.view"}}
                  </LinkTo>
                {{else}}
                  {{! Legacy PSLF runs used the old estimator's data shape, which the
                      sort-plans results view can't render — show them for reference
                      (see the info note) but without a link. The em dash is decorative
                      (hidden from assistive tech); the sr-only text explains the absence. }}
                  <span aria-hidden="true" class="tio-fg-secondary">&mdash;</span>
                  <span class="sr-only">{{t
                      "forgiveness_estimator_0726.history.legacy_info"
                    }}</span>
                {{/if}}
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
      </Table>
    </:body>
  </Section>
</template>;

export default UsageHistory;
